<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
  inboxId: { type: [String, Number], default: undefined },
});

const { t } = useI18n();
const store = useStore();

const contactGetter = useMapGetter('contacts/getContact');
const contact = computed(() => contactGetter.value(props.contactId) || {});

const isLoading = ref(false);
const isLoaded = ref(false);
const joined = ref([]);
const available = ref([]);
const search = ref('');
const isPickerOpen = ref(false);
const addingJid = ref(null);

const contactJid = computed(() => {
  const digits = (contact.value.phone_number || '').replace(/\D/g, '');
  return digits ? `${digits}@s.whatsapp.net` : '';
});

const availableFiltered = computed(() => {
  const query = search.value.trim().toLowerCase();
  if (!query) return available.value;
  return available.value.filter(group => (group.name || '').toLowerCase().includes(query));
});

const load = async () => {
  if (!props.contactId || !props.inboxId) return;
  isLoading.value = true;
  try {
    const data = await store.dispatch('inboxes/fetchWhatsappGoContactGroups', {
      inboxId: props.inboxId,
      contactId: props.contactId,
    });
    joined.value = data?.joined || [];
    available.value = data?.available || [];
    isLoaded.value = true;
  } catch {
    // Not a WhatsApp-Go inbox, or the gateway is unavailable — stay hidden.
    isLoaded.value = false;
  } finally {
    isLoading.value = false;
  }
};

watch([() => props.contactId, () => props.inboxId], () => load(), { immediate: true });

const addToGroup = async group => {
  if (!contactJid.value || addingJid.value) return;
  addingJid.value = group.jid;
  try {
    const response = await store.dispatch('inboxes/updateWhatsappGoGroupParticipant', {
      inboxId: props.inboxId,
      groupJid: group.jid,
      action: 'add',
      participants: [contactJid.value],
    });
    useAlert(
      response?.joinRequests
        ? t('INBOX_MGMT.ADD.WHATSAPP.RUUT_NATIVE.GROUPS.PARTICIPANTS_JOIN_REQUEST')
        : t('INBOX_MGMT.ADD.WHATSAPP.RUUT_NATIVE.GROUPS.PARTICIPANTS_ADDED')
    );
    joined.value = [...joined.value, group];
    available.value = available.value.filter(item => item.jid !== group.jid);
    isPickerOpen.value = false;
    search.value = '';
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.ADD.WHATSAPP.RUUT_NATIVE.GROUPS.PARTICIPANT_ERROR'));
  } finally {
    addingJid.value = null;
  }
};
</script>

<template>
  <div v-if="isLoading" class="flex items-center justify-center py-8 text-n-slate-11">
    <Spinner />
  </div>
  <div v-else-if="isLoaded">
    <div class="px-4 pt-3 pb-2">
      <NextButton
        v-if="!isPickerOpen"
        ghost
        xs
        icon="i-lucide-plus"
        :label="t('CONVERSATION_SIDEBAR.CONTACT_GROUPS.ADD_TO_GROUP')"
        @click="isPickerOpen = true"
      />
      <template v-else>
        <Input
          v-model="search"
          type="search"
          :placeholder="t('CONVERSATION_SIDEBAR.CONTACT_GROUPS.SEARCH_PLACEHOLDER')"
        />
        <div class="flex flex-col gap-0.5 mt-2 overflow-y-auto max-h-56">
          <p v-if="!availableFiltered.length" class="px-2 py-3 text-sm text-n-slate-11">
            {{ t('CONVERSATION_SIDEBAR.CONTACT_GROUPS.NO_AVAILABLE') }}
          </p>
          <button
            v-for="group in availableFiltered"
            :key="group.jid"
            type="button"
            class="flex items-center w-full gap-2 px-2 py-1.5 text-left rounded-lg hover:bg-n-slate-3 disabled:opacity-50"
            :disabled="!!addingJid"
            @click="addToGroup(group)"
          >
            <Icon icon="i-lucide-plus" class="text-n-slate-11 size-4 shrink-0" />
            <span class="text-sm truncate text-n-slate-12">{{ group.name }}</span>
            <Spinner v-if="addingJid === group.jid" class="ml-auto size-4" />
          </button>
        </div>
      </template>
    </div>

    <p v-if="!joined.length" class="px-6 py-6 text-sm leading-6 text-center text-n-slate-11">
      {{ t('CONVERSATION_SIDEBAR.CONTACT_GROUPS.EMPTY') }}
    </p>
    <div v-else class="flex flex-col">
      <div v-for="group in joined" :key="group.jid" class="flex items-center gap-2 px-4 py-2">
        <span
          class="flex items-center justify-center shrink-0 rounded-full size-6 bg-n-slate-4 text-xs font-medium text-n-slate-11"
        >
          {{ (group.name || '?').charAt(0).toUpperCase() }}
        </span>
        <span class="text-sm truncate text-n-slate-12">{{ group.name }}</span>
      </div>
    </div>
  </div>
</template>
